import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { canAccess } from '../config/rbac.js';

/**
 * Authentication + role-based route guard. Replaces the auth-only ProtectedRoute
 * for the app's authenticated routes:
 *
 *   - Not logged in        → redirect to /login.
 *   - Logged in, no access → redirect to /dashboard (the safe per-user landing)
 *     with a `denied` flag, so unauthorized direct navigation is blocked.
 *   - Authorized           → render the route.
 *
 * Authorized roles per path come from config/rbac.js (the same source AppShell
 * uses to hide unauthorized nav items).
 */
export default function RoleRoute() {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!canAccess(user?.role, location.pathname)) {
    return <Navigate to="/dashboard" replace state={{ denied: location.pathname }} />;
  }

  return <Outlet />;
}
